/**
 * Table parts shared by the admin screens.
 *
 * The reason these exist: every admin table had hand-rolled header classes and
 * put identifiers in `<td dir="ltr">`. Flipping the *cell* also flips its text
 * alignment, so an LTR cell aligned left while its RTL neighbour aligned right
 * and the two ran together ("אתריםATARIM"). `Ltr` keeps the direction on an
 * inline span so the cell — and its alignment with the header — stays put.
 */
import type { ReactNode } from 'react';
import { cn } from '../../utils.js';

export function Table({
  children,
  minWidth = '30rem',
  className,
}: {
  children: ReactNode;
  /** Below this the wrapper scrolls instead of squashing the columns. */
  minWidth?: string;
  className?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full text-sm', className)} style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

export function TH({
  children,
  align = 'start',
  className,
}: {
  children?: ReactNode;
  align?: 'start' | 'end';
  className?: string;
}) {
  return (
    <th
      className={cn(
        'px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground',
        align === 'end' ? 'text-end' : 'text-start',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TD({
  children,
  align = 'start',
  className,
  title,
  colSpan,
}: {
  children?: ReactNode;
  align?: 'start' | 'end';
  className?: string;
  title?: string;
  /** For a full-width detail row sitting under its own record. */
  colSpan?: number;
}) {
  return (
    <td
      className={cn('px-3 py-3', align === 'end' ? 'text-end' : 'text-start', className)}
      title={title}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}

export function THeadRow({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-border align-bottom">{children}</tr>
    </thead>
  );
}

export function TR({ children, className }: { children: ReactNode; className?: string }) {
  return <tr className={cn('border-b border-border/50 last:border-0', className)}>{children}</tr>;
}

/** Latin text (ids, emails, dates) inside an RTL cell — direction without the alignment flip. */
export function Ltr({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span dir="ltr" className={cn('inline-block', className)}>
      {children}
    </span>
  );
}

/** An id: Latin, monospace, muted. */
export function IdText({ children, className }: { children: ReactNode; className?: string }) {
  return <Ltr className={cn('font-mono text-xs text-muted-foreground', className)}>{children}</Ltr>;
}
